import { useCallback, useRef } from "react";

import { SILENCE_RMS_THRESHOLD } from "./useSpeechToTextRecorder";

// Web Audio analyser values are tuned for voice activity detection, not visualization.
const AUDIO_SIGNAL_MONITOR_FFT_SIZE = 512;
const AUDIO_SIGNAL_MONITOR_SMOOTHING_TIME_CONSTANT = 0.85;

// Tracks raw audio activity (RMS threshold) for the current stream.
// This hook only answers "signal detected or not".
const useAudioSignalMonitor = ({
  threshold = SILENCE_RMS_THRESHOLD,
  fftSize = AUDIO_SIGNAL_MONITOR_FFT_SIZE,
  smoothingTimeConstant = AUDIO_SIGNAL_MONITOR_SMOOTHING_TIME_CONSTANT
} = {}) => {
  const hasAudioSignalRef = useRef(false);
  const signalMonitorRef = useRef(null);

  const stopSignalMonitor = useCallback(() => {
    const monitor = signalMonitorRef.current;
    signalMonitorRef.current = null;

    if (!monitor) return;

    if (monitor.animationFrameId) {
      cancelAnimationFrame(monitor.animationFrameId);
    }

    monitor.source.disconnect();
    monitor.audioContext.close();
  }, []);

  const startSignalMonitor = useCallback(
    stream => {
      stopSignalMonitor();
      hasAudioSignalRef.current = false;

      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextClass) {
        // Older browsers can still record; skip signal-gating to avoid false negatives.
        hasAudioSignalRef.current = true;
        return;
      }

      const audioContext = new AudioContextClass();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = fftSize;
      analyser.smoothingTimeConstant = smoothingTimeConstant;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      const signalData = new Float32Array(analyser.fftSize);
      const monitor = {
        analyser,
        audioContext,
        source,
        animationFrameId: null
      };

      signalMonitorRef.current = monitor;

      const measureSignal = () => {
        analyser.getFloatTimeDomainData(signalData);

        // RMS on time-domain signal gives a stable voice/noise activity indicator.
        const squaredSum = signalData.reduce((sum, value) => {
          return sum + value * value;
        }, 0);
        const rms = Math.sqrt(squaredSum / signalData.length);

        if (rms >= threshold) {
          hasAudioSignalRef.current = true;
        }

        monitor.animationFrameId = requestAnimationFrame(measureSignal);
      };

      measureSignal();
    },
    [fftSize, smoothingTimeConstant, stopSignalMonitor, threshold]
  );

  return {
    hasAudioSignalRef,
    startSignalMonitor,
    stopSignalMonitor
  };
};

export default useAudioSignalMonitor;
